import React, { useState, useEffect } from 'react';
import { ResumeMetadataCondition } from '@scale/shared/onboardingControlPanel/types/edge';
import { fetchPaginatedResumeMetadataValues } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { ResumeMetadataFields } from '@scale/shared-types';
import { SelectMenu } from '@scale/scaleui';
import { TextField as MuiTextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

interface ResumeMetadataConditionValueInputProps {
  value: ResumeMetadataCondition;
  onChange: (value: ResumeMetadataCondition) => void;
  disabled?: boolean;
}

const ResumeMetadataConditionValueInput: React.FC<ResumeMetadataConditionValueInputProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const [resumeMetadataCondition, setResumeMetadataCondition] = useState<ResumeMetadataCondition>(
    value || {},
  );
  const [resumeMetadataValueOptions, setResumeMetadataValueOptions] = useState<string[]>([]);
  const [resumeMetadataValueOptionCounts, setResumeMetadataValueOptionCounts] = useState<
    Map<string, number>
  >(new Map());
  const [resumeMetadataSearch, setResumeMetadataSearch] = useState('');

  const handleFetchResumeMetadataValues = async (field: ResumeMetadataFields, search?: string) => {
    const { values: resumeMetadataValues } = await fetchPaginatedResumeMetadataValues(
      field,
      search,
    );
    setResumeMetadataValueOptions(resumeMetadataValues.map(value => value.value));
    setResumeMetadataValueOptionCounts(
      new Map(resumeMetadataValues.map(value => [value.value, value.count])),
    );
  };

  useEffect(() => {
    handleFetchResumeMetadataValues(
      resumeMetadataCondition.field as ResumeMetadataFields,
      resumeMetadataSearch,
    );
  }, [resumeMetadataCondition.field, resumeMetadataSearch]);

  return (
    <>
      <SelectMenu
        className="flex"
        options={Object.values(ResumeMetadataFields).map(field => ({
          id: field,
          name: field,
        }))}
        setSelected={e => {
          setResumeMetadataCondition({ ...resumeMetadataCondition, values: [], field: e });
          setResumeMetadataSearch('');
          onChange({ ...resumeMetadataCondition, values: [], field: e });
        }}
        selectedOptionId={resumeMetadataCondition.field}
        disabled={disabled}
      />

      <Autocomplete
        multiple
        disablePortal
        loading={resumeMetadataValueOptions.length === 0}
        onChange={(_event, newValues) => {
          setResumeMetadataCondition({ ...resumeMetadataCondition, values: newValues });
          onChange({ ...resumeMetadataCondition, values: newValues });
        }}
        value={resumeMetadataCondition.values || []}
        id="resume-metadata-values-input"
        options={resumeMetadataValueOptions}
        size="small"
        fullWidth={true}
        onInputChange={(_event, newInputValue) => {
          setResumeMetadataValueOptions([]);
          setResumeMetadataSearch(newInputValue);
        }}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span>{option}</span>
            {resumeMetadataValueOptionCounts.get(option) && (
              <span style={{ fontSize: '0.8em', marginLeft: 'auto', textAlign: 'right' }}>
                ({resumeMetadataValueOptionCounts.get(option)})
              </span>
            )}
          </li>
        )}
        renderInput={params => <MuiTextField {...params} />}
        disabled={disabled}
        filterOptions={options => options} // We filter and order on the backend, so disable filtering
      />
    </>
  );
};

export default ResumeMetadataConditionValueInput;
